import React, {useEffect, useMemo, useRef, useState} from 'react';
import {ActivityIndicator, Image, Platform, View} from 'react-native';
import {useNavigation, useRoute} from '@react-navigation/native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import useGetTournamentsList from '@/Hooks/TournamentsApi/useGetTournamentsList';
import useGetMyJoinedTournaments from '@/Hooks/TournamentsApi/useGetMyJoinedTournaments';
import {
  useRoyaleTournaments,
  type RoyaleTournamentSummary,
} from '@/Hooks/FunChallengesApi/useRoyaleTournaments';
import NoTournamentFound from '@/Assets/Images/Tournaments/no_tournament_found.svg';
import useIsMobile from '@/Hooks/useIsMobile';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import TournamentRow from './TournamentRow';
import {RFPercentage} from 'react-native-responsive-fontsize';
import Pagination from '@/Screens/Games/Pagination';

interface TournamentsProps {
  getTournamentDetails: (tournamentId: number) => void;
  activeTab: string;
  userId: string;
  newLayout: boolean;
  withdraw: boolean;
}

/**
 * Normalize a Puzzle Royale tournament into the SAME row shape TournamentRow
 * consumes, so royale + chess tournaments render as one merged list. Tagged
 * `kind:'royale'` + `royaleId` so handleRowPress routes it to the durable
 * royale route (NOT the chess getTournamentDetails path — different id space,
 * pitfall 34). `time_control` is REQUIRED (TournamentRow splits it on '+'
 * unconditionally) so we synthesize `${timer_sec}+0`; classifyTimeControl then
 * picks the icon. is_free:true → the "Free" pill (royale is always free).
 */
function normalizeRoyaleRow(
  t: RoyaleTournamentSummary,
  myUserId: string,
  // True when this row came from the `mine=1` (Joined) fetch, i.e. every row in
  // it IS one of my registrations — that's what drives the green tick.
  isMine = false,
) {
  return {
    // Prefix the id so it can never collide with a chess tournament id in the
    // merged list's React keys / sort; the real numeric id is on `royaleId`.
    id: `pr_${t.id}`,
    royaleId: t.id,
    kind: 'royale' as const,
    name: (t.name && t.name.trim()) || `Puzzle Royale #${t.id}`,
    start_date: t.scheduled_at || t.started_at || t.ended_at || '',
    time_control: `${t.timer_sec || 0}+0`,
    rounds: 1,
    tournament_type: 'Puzzle_Royale',
    is_free: true,
    is_active: t.state === 'live',
    is_private: false,
    // NOTE: this is TournamentRow's CHESS row-shape field (it gates the paid-entry
    // `archive-edit` icon), NOT the royale API's `is_registered`. Royale is always
    // free, so it stays false — don't wire the API flag here or a registered
    // royale would render the chess "pay to enter" glyph.
    is_registered: false,
    // "joined" tick = I hold a registered roster row, which turns TournamentRow's
    // circular arrow button into a circular green tick.
    //
    // Prefer the SERVER's authoritative `is_registered` (present on any current
    // backend, correct on every tab); fall back to the caller's `mine=1`-derived
    // hint only when the field is absent on an older build. `??` not `||` so a
    // server-sent `false` is respected instead of being overridden by the hint.
    is_joined: t.is_registered ?? isMine,
    cash_prize: 0,
    _royaleState: t.state,
    _myUserId: myUserId,
  };
}

const Tournaments: React.FC<TournamentsProps> = ({
  getTournamentDetails,
  activeTab,
  userId,
  newLayout: _newLayout,
  withdraw,
}) => {
  const isMobileOrTab = useIsMobile();
  const navigation = useNavigation();
  const [tableData, setTableData] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const userData = useLoggedInUserStore(state => state.userData);

  const paginate = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };
  const tournamentData = [
    {
      time: '10:00 AM',
      name: 'Rapid Swiss Tournament',
      startingIn: '2 hrs',
      players: 40,
      fees: '₹1000',
      url: 'https://example.com/join/1',
      id: 1,
    },
    {
      time: '12:00 PM',
      name: 'Blitz Open Tournament',
      startingIn: '1 hr',
      players: 20,
      fees: 'Free',
      url: 'https://example.com/join/2',
      id: 2,
    },
    {
      time: '2:00 PM',
      name: 'Classic Chess Tournament',
      startingIn: '3 hrs',
      players: 50,
      fees: '₹500',
      url: 'https://example.com/join/3',
      id: 3,
    },
  ];

  const tournamentsDataref = useRef(tournamentData);

  const {
    mutate: fetchTournaments,
    data: tournamentsData,
    isLoading: listLoading,
  } = useGetTournamentsList();

  const {mutate: fetchMyTournaments, data: myTournamentsData} =
    useGetMyJoinedTournaments();

  // Puzzle Royale tournaments merged into the SAME lists (no separate tab):
  //  - Upcoming  → scheduled + live (everyone's).
  //  - Joined    → mine=1 across scheduled/live/completed (my registrations).
  // Read-only REST; polled while a tab is open. Enabled only for the tab that
  // needs it so the other tab doesn't fire an unused request.
  const {data: royaleUpcoming} = useRoyaleTournaments(['scheduled', 'live'], {
    enabled: activeTab === 'upcoming',
    refetchIntervalMs: 60000,
  });
  const {data: royaleJoined} = useRoyaleTournaments(
    ['scheduled', 'live', 'completed'],
    {enabled: activeTab === 'registered', mine: true, refetchIntervalMs: 60000},
  );

  // My registrations, fetched ALSO on the Upcoming tab purely to drive the
  // green tick there. The list payload (RoyaleTournamentSummary) carries no
  // per-row membership flag, so "am I registered?" cannot be derived from the
  // Upcoming response alone — but `mine=1` returns exactly my registrations, so
  // intersecting the two by id gives it without any backend change. Same query
  // key as the Joined tab's fetch, so react-query dedupes/shares the cache
  // rather than issuing a second request when both are mounted.
  const {data: royaleMineForTick} = useRoyaleTournaments(
    ['scheduled', 'live', 'completed'],
    {enabled: activeTab === 'upcoming', mine: true, refetchIntervalMs: 60000},
  );
  const myRoyaleIds = useMemo(
    () => new Set((royaleMineForTick || []).map(t => String(t.id))),
    [royaleMineForTick],
  );

  const POLL_INTERVAL = 300000;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let interval;
    setCurrentPage(1);
    const fetchData = () => {
      if (activeTab === 'upcoming' || withdraw) {
        fetchTournaments({limit: 25, offset: 0, userId: userId});
      } else if (activeTab === 'registered') {
        fetchMyTournaments({userId});
      }
    };

    if (userId) {
      fetchData();
    }

    if (activeTab === 'upcoming') {
      interval = setInterval(fetchData, POLL_INTERVAL);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [activeTab, withdraw, userId]);

  type RouteParams = {highlightedTournamentId?: number};
  const route = useRoute();
  const {highlightedTournamentId: routeHighlightedTournamentId} =
    (route.params || {}) as RouteParams;

  useEffect(() => {
    if (activeTab === 'upcoming' && tournamentsData?.data) {
      const itemsPerPage = 10;
      // Merge royale rows (scheduled/live) into the chess Upcoming list, sorted
      // ascending by start time alongside chess tournaments. The pinned #1591
      // banner is pulled out first and always stays at the very top of page 1,
      // exactly as before — royale rows only join the normal (non-pinned) pool.
      const royaleRows = (royaleUpcoming || []).map(t =>
        // isMine from the `mine=1` id set — that's what turns the row's yellow
        // arrow into the green tick on THIS tab too (the Upcoming payload alone
        // has no membership flag).
        normalizeRoyaleRow(t, userId, myRoyaleIds.has(String(t.id))),
      );
      const allData = [...tournamentsData.data, ...royaleRows];
      const fixedTournamentIndex = allData.findIndex(item => item.id === 1591);
      const fixedTournament =
        fixedTournamentIndex !== -1
          ? allData.splice(fixedTournamentIndex, 1)[0]
          : null;
      // Sort the non-pinned pool by start time (chess + royale interleaved).
      allData.sort(
        (a, b) =>
          new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
      );
      const totalItems = allData.length + (fixedTournament ? 1 : 0);
      setTotalPages(Math.ceil(totalItems / itemsPerPage));

      const paginatedData: any[] = [];

      if (currentPage === 1) {
        if (fixedTournament) paginatedData.push(fixedTournament);
        paginatedData.push(
          ...allData.slice(0, itemsPerPage - paginatedData.length),
        );
      } else {
        const startIndex =
          (currentPage - 1) * itemsPerPage - (fixedTournament ? 1 : 0);
        const endIndex = startIndex + itemsPerPage;
        paginatedData.push(...allData.slice(startIndex, endIndex));
      }
      setTableData(paginatedData);
      tournamentsDataref.current = tournamentsData.data;
    } else {
      const itemsPerPage = 10;
      // Merge my royale registrations into the chess Joined list.
      const royaleRows = (royaleJoined || []).map(t =>
        normalizeRoyaleRow(t, userId, true),
      );
      const merged = [...(myTournamentsData?.data || []), ...royaleRows];
      // Newest first, by FULL start timestamp.
      //
      // The old comparator sorted the DAY descending but the time within that
      // day ASCENDING, so everything scheduled today listed oldest-first
      // (10:13 AM at the top, 12:56 PM at the bottom) while yesterday's block
      // sat below today's. That reads as "wrong order" for a Joined list whose
      // whole point is "what did I just play / what's next". One consistent
      // descending sort on getTime() fixes both halves.
      merged.sort(
        (a, b) =>
          new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
      );

      const totalItems = merged.length;

      setTotalPages(Math.ceil(totalItems / itemsPerPage));
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = merged.slice(startIndex, endIndex);
      setTableData(paginatedData);
    }
  }, [
    currentPage,
    tournamentsData,
    myTournamentsData,
    activeTab,
    royaleUpcoming,
    royaleJoined,
    // so the tick appears as soon as the `mine=1` fetch resolves, without
    // needing a tab switch or the 60s poll to rebuild the rows.
    myRoyaleIds,
    userId,
  ]);

  const timeControlToSvgAndColor = {
    Bullet: {
      svg: (
        <Image
          source={require('@/Assets/Images/Svgs/Bullet.svg')}
          style={{
            width: 20,
            height: 20,
          }}
        />
      ),
      color: '#937ADB47',
    },
    Bolt: {
      svg: (
        <Image
          source={require('@/Assets/Images/Svgs/Bolt.svg')}
          style={{
            width: 20,
            height: 20,
          }}
        />
      ),
      color: '#67B3E147',
    },
    Rapid: {
      svg: (
        <Image
          source={require('@/Assets/Images/Svgs/Rapid.svg')}
          style={{
            width: 20,
            height: 20,
          }}
        />
      ),
      color: '#DB7ACC48',
    },
    Snail: {
      svg: (
        <Image
          source={require('@/Assets/Images/Svgs/Snail.svg')}
          style={{
            width: 20,
            height: 20,
          }}
        />
      ),
      color: '#3DAB9E47',
    },
  };

  const handleRowPress = (tournament: any) => {
    // Puzzle Royale rows route to the DURABLE royale route (own id space) —
    // NEVER through getTournamentDetails / setParams({tournamentId}), which
    // drive the chess-tournament lobby and would open an unrelated chess
    // tournament for a royale id (pitfall 34).
    if (tournament?.kind === 'royale') {
      // @ts-ignore
      navigation.navigate('PuzzleRoyaleTournament', {
        // DISTINCT param name from the chess `tournamentId` — see pitfall 34.
        // Sharing the name let this royale id merge onto the CCTournaments route
        // and open the chess tournament of the same number.
        royaleTournamentId: tournament.royaleId,
      });
      return;
    }
    const tournamentId = tournament.id;
    // @ts-ignore
    navigation.setParams({
      tournamentId,
      tournamentName: tournament?.name,
    });
    getTournamentDetails(tournamentId);
  };

  const sortedTournaments = tableData?.slice()?.sort((a, b) => {
    if (a.id === 1591) return -1;
    if (b.id === 1591) return 1;
    return 0;
  });

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      style={{
        borderRadius: 8,
        backgroundColor: CCColors.White,
        height: !userId || listLoading ? '50%' : 'auto',
      }}>
      {tableData && tableData.length > 0 ? (
        <Box
          flexDirection="column"
          flex={1}
          style={{
            width: '100%',
            marginHorizontal: !isMobileOrTab && 5,
            padding: isMobileOrTab ? 10 : 20,
          }}>
          <Box
            flexDirection="row"
            style={{marginBottom: 10}}
            alignItems="center">
            <CCText
              color={CCColors.TextColor.LightGrey}
              fontWeight={500}
              fontSize={!isMobileOrTab ? FontSizes[14] : RFPercentage(1.8)}
              style={{marginRight: '20%'}}>
              Time
            </CCText>
            <CCText
              color={CCColors.TextColor.LightGrey}
              fontWeight={500}
              fontSize={!isMobileOrTab ? FontSizes[14] : RFPercentage(1.8)}>
              Tournament Details
            </CCText>
          </Box>
          <Box
            style={{
              borderTopColor: CCColors.BorderLight,
              borderTopWidth: 1,
            }}
            flexDirection="column">
            {sortedTournaments?.map((item, rowIndex) => (
              <TournamentRow
                key={item.id ?? rowIndex}
                item={item}
                rowIndex={rowIndex}
                activeTab={activeTab}
                handleRowPress={handleRowPress}
                timeControlToSvgAndColor={timeControlToSvgAndColor}
                isMobileOrTab={isMobileOrTab}
                userId={userId}
                userData={userData}
                isHighlighted={item.id === Number(routeHighlightedTournamentId)}
              />
            ))}
          </Box>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            paginate={paginate}
          />
        </Box>
      ) : !userId || listLoading ? (
        <Box alignItems="center" justifyContent="center">
          <ActivityIndicator color={CCColors.BlueAqua} size={'large'} />
        </Box>
      ) : (
        <View
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            gap: 20,
            marginTop: isMobileOrTab ? 0 : '10%',
          }}>
          <Image
            resizeMode="contain"
            style={{
              width: isMobileOrTab ? Platform.OS === 'web' && 100 : 180,
              height: isMobileOrTab ? Platform.OS === 'web' && 100 : 180,
            }}
            source={NoTournamentFound}
          />
          <CCText fontSize={24} style={{ lineHeight: 24, fontWeight: 500 }}>
            Nothing scheduled today
          </CCText>
        </View>
      )}
    </Box>
  );
};

export default Tournaments;
